/**
 * BaseNode - base class for all nodes
 *
 * Provides:
 * - Endpoint creation from configs
 * - Input handling with clamping/scaling
 * - Output emission
 * - Runtime access via bound functions (no IDs needed)
 */

import { INode, LogLevel } from './INode';
import { Dependencies, EndpointApi } from './Dependencies';
import type { Endpoint } from './Endpoint';
import type { AnimationLoop } from '../runtime/AnimationLoop';

import type { ITransport } from '../runtime/ITransport';
import type { IEndpoint } from '../runtime/IEndpoint';
import type { TypedParams, TypedTriggers, TypedEvents, EndpointsRecord, TypedInternal } from './TypedEndpoints';
import type { Payload, PayloadMetadata } from '../graph/Payload';
import { resolveConfigValue } from '../runtime/util';
import { logger } from '../logging';

export type { LogLevel } from './INode';

const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  [LogLevel.Error]: 1,
  [LogLevel.Info]: 2,
  [LogLevel.Trace]: 3
};



export abstract class BaseNode<T extends EndpointsRecord = EndpointsRecord> implements INode {
  readonly id: string;
  readonly type: string;
  protected transport: ITransport;
  private endpointApi: EndpointApi;
  protected deps: Dependencies;

  /** Typed accessor for parameter values: this.params.attack */
  params: TypedParams<T>;

  /** Typed accessor for trigger endpoints: this.triggers.tick.emit() */
  protected triggers: TypedTriggers<T>;

  /** Typed accessor for event endpoints: this.events.event.emit(payload) */
  protected events: TypedEvents<T>;

  /** Typed accessor for event endpoints: this.events.event.emit(payload) */
  protected internals: TypedInternal<T>;

  /** Log level for this node */
  logLevel: LogLevel = LogLevel.Error;

  constructor(
    id: string,
    // _endpoints: T,  // Used only for type inference (object format)
    deps: Dependencies
  ) {
    this.id = id;
    this.type = deps.type;
    this.deps = deps;
    this.transport = deps.transport;
    this.endpointApi = deps.endpointApi;
    // this.animationLoop = deps.animationLoop;

    // Create typed proxy for parameter access
    // Returns ParamAccessor objects with value, set(), and metadata
    this.params = new Proxy({} as TypedParams<T>, {
      get: (_, key: string) => {
        const metadata = this.getMetadata(key);
        if (!metadata) throw new Error(`[${this.id}] param '${key}' does not exist`);
        // Exclude audio endpoints - they're connections, not parameters
        if (metadata.dataFlow !== 'param') {
          throw new Error(`[${this.id}] '${key}' is not a parameter`);
        }
        // Return ParamAccessor with value getter, setter, and metadata
        const endpointApi = this.endpointApi;
        return {
          get value() {
            // Unwrap payload to get raw value
            return resolveConfigValue(endpointApi.getValue(key));
          },
          set: (value: any) => {
            endpointApi.sendOutput(key, value);
          },
          dataType: metadata.dataType,
          min: metadata.min,
          max: metadata.max,
          step: metadata.step,
        };
      }
    });

    // Create typed proxy for trigger endpoints
    this.triggers = new Proxy({} as TypedTriggers<T>, {
      get: (_, key: string) => {
        const metadata = this.getMetadata(key);
        if (!metadata) throw new Error(`[${this.id}] trigger '${key}' does not exist`);
        if (metadata.dataFlow !== 'trigger') {
          throw new Error(`[${this.id}] '${key}' is not a trigger endpoint`);
        }
        return {
          emit: (metadata: PayloadMetadata) => {
            this.endpointApi.sendOutput(key, { value: true, ...metadata });
          }
        };
      }
    });

    // Create typed proxy for event endpoints
    this.events = new Proxy({} as TypedEvents<T>, {
      get: (_, key: string) => {
        const metadata = this.getMetadata(key);
        if (!metadata) throw new Error(`[${this.id}] event '${key}' does not exist`);
        if (metadata.dataFlow !== 'event') {
          throw new Error(`[${this.id}] '${key}' is not an event endpoint`);
        }
        return {
          emit: (payload: Payload) => {
            this.endpointApi.sendOutput(key, payload);
          }
        };
      }
    });

    // Create typed proxy for internal data endpoints
    this.internals = new Proxy({} as TypedInternal<T>, {
      get: (_, key: string) => {
        const metadata = this.getMetadata(key);
        if (!metadata) throw new Error(`[${this.id}] internal '${key}' does not exist`);
        if (metadata.dataFlow !== 'internal') {
          throw new Error(`[${this.id}] '${key}' is not internal data`);
        }
        return {
          set: (payload: Payload) => {
            this.endpointApi.setValue(key, payload);
          }
        };
      }
    });

  }

  getMetadata(endpointId) {
    return this.endpointApi.getMetadata(endpointId)
  }

  /**
   * Check if a log level should be output based on current logLevel
   */
  private shouldLog(level: LogLevel): boolean {
    return LOG_LEVEL_PRIORITY[level] <= LOG_LEVEL_PRIORITY[this.logLevel];
  }

  /**
   * Internal log method that routes to LogService
   */
  private log(level: LogLevel, ...args: any[]): void {
    if (!this.shouldLog(level)) return;
    const message = args.map(a => {
      if (a instanceof Error) return a.message + (a.stack ? '\n' + a.stack : '');
      if (typeof a === 'string') return a;
      if (typeof a === 'number' || typeof a === 'boolean') return String(a);
      // Objects may have circular refs (Three.js, AudioContext, etc.)
      // Just show type info instead of failing
      if (a && typeof a === 'object') {
        const ctor = a.constructor?.name ?? 'Object';
        return `[${ctor}]`;
      }
      return String(a);
    }).join(' ');
    logger.log(level, message, this.type, this.id);
  }

  /** Log an error */
  protected logError(...args: any[]): void {
    this.log(LogLevel.Error, ...args);
  }

  /** Log info (shown if logLevel >= 'info') */
  protected logInfo(...args: any[]): void {
    this.log(LogLevel.Info, ...args);
  }

  /** Log trace info (shown if logLevel >= 'trace') */
  protected logTrace(...args: any[]): void {
    this.log(LogLevel.Trace, ...args);
  }

  /** Whether any param has a compiled expression getter (useExp). Used by GraphIndex/SceneDescriptor. */
  hasFunctionParams(): boolean {
    return this.deps.endpointApi.hasFunctionParams();
  }

  // ─────────────────────────────────────────────────────────────
  // Lifecycle
  // ─────────────────────────────────────────────────────────────

  /** Per-frame animation: evaluate expressions and push output. Override in subclasses. */
  animate(): void {
    // No-op base. Subclasses override to read params (evaluating expression getters) and push output.
  }

  initialize(): void {
    // (e.g., audio nodes creating synths, schedulers setting up callbacks)
  }

  dispose(): void {
    // Override in subclasses
  }

  // ─────────────────────────────────────────────────────────────
  // Connection Lifecycle Hooks
  // ─────────────────────────────────────────────────────────────

  /**
   * Called on TARGET node when something connects to it.
   * Override in subclasses to react to incoming connections.
   */
  onConnectedFrom(endpointId: string, source: IEndpoint): void {
    // Override in subclasses
  }

  /**
   * Called on TARGET node when something disconnects from it.
   * Override in subclasses to react to disconnections.
   * @param source - The source endpoint that was disconnected
   */
  onDisconnectedFrom(endpointId: string, source: IEndpoint): void {
    // Override in subclasses
  }

  /**
   * Called on SOURCE node when it connects to something.
   * Override in subclasses (e.g., audio nodes) to set up connections.
   */
  onConnectedTo(endpointId: string, targetNode: INode, targetEndpoint: IEndpoint): void {
    // Override in subclasses
  }

  /**
   * Called on SOURCE node when it disconnects from something.
   * Override in subclasses (e.g., audio nodes) to tear down connections.
   */
  onDisconnectedTo(endpointId: string, targetNode: INode, targetEndpointId: string): void {
    // Override in subclasses
  }

  // ─────────────────────────────────────────────────────────────
  // Input Handling - implements INode interface
  // ─────────────────────────────────────────────────────────────

  /**
   * Param value (from config, UI tweak, or connection).
   * @param source - undefined for config/UI, IEndpoint for connection data
   */
  onParamChanged(payload: Payload, endpointId: string, source?: IEndpoint): void {
  }

  /**
   * Internal data (always from connection).
   */
  onInternalChanged(payload: Payload, endpointId: string, source: IEndpoint): void {
  }

  /**
   * Event data (always from connection).
   */
  handleEvent(payload: Payload, endpointId: string, source: IEndpoint): void {
  }

  /**
   * Trigger (always from connection).
   */
  handleTrigger(payload: Payload, endpointId: string, source: IEndpoint): void {
  }

  /**
   * Called when a connection is removed.
   * Override to clean up data from that source.
   */
  onDisconnected(endpointId: string, source: IEndpoint): void {
  }

  // ─────────────────────────────────────────────────────────────
  // Endpoint Metadata
  // ─────────────────────────────────────────────────────────────

  /**
   * Set endpoint disabled state.
   * Updates the containing definition's config.
   */
  protected setEndpointDisabled(endpointId: string, disabled: boolean): void {
    this.endpointApi.setDisabled(endpointId, disabled);
  }

  /**
   * Set endpoint metadata (min, max, step, options, optionLabels, optionInfo).
   * Updates the containing definition's config.
   */
  protected setEndpointMetadata(endpointId: string, metadata: Record<string, any>): void {
    this.endpointApi.setMetadata(endpointId, metadata);
  }

  /**
   * Add a custom param (persisted in definition config).
   * Used by Repeater, Switch, etc. for user-facing dynamic params.
   */
  protected addCustomParam(endpointId: string, config?: { value?: any; dataType?: string; label?: string; dataFlow?: string; isInput?: boolean; isOutput?: boolean; group?: string }): void {
    this.endpointApi.addCustomParam(endpointId, config);
  }

  /**
   * Remove a custom param.
   */
  protected removeCustomParam(endpointId: string): void {
    this.endpointApi.removeCustomParam(endpointId);
  }

  /**
   * Add a transient endpoint (not persisted — created/removed at runtime).
   * Used by nodes that need runtime-only endpoints (not persisted).
   */
  protected addEndpoint(endpointId: string, config: Endpoint): void {
    this.endpointApi.addEndpoint(endpointId, config);
  }

  /**
   * Remove a transient endpoint.
   */
  protected removeEndpoint(endpointId: string): void {
    this.endpointApi.removeEndpoint(endpointId);
  }

}

